import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { Loader2, Gift } from "lucide-react";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, FieldShell } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import {
  addPartnerToNetwork,
  editNetworkPartner,
  searchPartnersByContact,
  triggerPartnerReward,
  getNetworkPartnerById,
  addContact,
  editContact,
  getContactById,
} from "./networkApi";

/**
 * Full-page add / edit forms for the Customer's Agents (network partners) and Friends (contacts).
 * Fields, validation rules, API calls and payloads are the ones the former popups used
 * (AddPartnerModal, EditPartnerModal, AddContactModal, EditContactModal).
 */
const FORM_ID = "network-form";

const KINDS = {
  agent: {
    listPath: "/customer/agents",
    label: "Agent",
    newTitle: "Add Network Partner",
    newSubtitle: "Connect an associate partner and earn reward points",
    editTitle: "Edit Partner",
    editSubtitle: "Update partner details",
  },
  friend: {
    listPath: "/customer/friends",
    label: "Friend",
    newTitle: "Add Friend / Customer",
    newSubtitle: "Add personal contacts and clients to your address book",
    editTitle: "Edit Contact",
    editSubtitle: "Update contact information",
  },
};

const EMPTY = {
  agent: {
    add: {
      first_name: "", last_name: "", mobile_number: "", email_address: "", address: "", address1: "", street: "",
      apartment: "", country_name: "India", state_name: "", city_name: "", zip_code: "", area_name: "",
    },
    edit: {
      id: "", first_name: "", last_name: "", mobile_number: "", email_address: "", country_name: "India",
      state_name: "", city_name: "", area_name: "",
    },
  },
  friend: {
    add: { first_name: "", last_name: "", mobile_number: "", email_address: "", address: "", area_name: "", zip_code: "" },
    edit: { id: "", first_name: "", last_name: "", mobile_number: "", email_address: "", address: "", area_name: "", zip_code: "" },
  },
};

// Same mapping the edit popups used when pre-filling from a record
const fromRecord = (kind, item) => {
  const base = {
    id: item.id,
    first_name: item.first_name || item.firstName || "",
    last_name: item.last_name || item.lastName || "",
    mobile_number: (item.mobile_number || item.mobileNo || "").replace(/^91/, ""),
    email_address: item.email_address || item.emailId || "",
    area_name: item.area_name || item.area || "",
  };
  return kind === "agent"
    ? {
        ...base,
        country_name: item.country_name || item.country || "India",
        state_name: item.state_name || item.state || "",
        city_name: item.city_name || item.city || "",
      }
    : { ...base, address: item.address || "", zip_code: item.zip_code || item.zipCode || "" };
};

// Adding needs a full 10-digit number (as the add popups checked); editing only needs one to be present
const buildSchema = (isEdit) =>
  Yup.object({
    first_name: Yup.string().trim().required("First Name is required"),
    mobile_number: isEdit
      ? Yup.string().trim().required("Mobile Number is required")
      : Yup.string().trim().min(10, "A valid 10-digit mobile number is required").required("A valid 10-digit mobile number is required"),
    email_address: Yup.string().email("Enter a valid email address"),
  });

/** Add-partner helper: look up registered partners and auto-fill the form (same as the popup) */
const PartnerSearch = () => {
  const { setValues } = useFormikContext();
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!keyword || keyword.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await searchPartnersByContact({ keyword_search: keyword.trim(), user_type: "PARTNER" });
        setResults(res?.users || res?.partners || []);
      } catch (err) {
        console.error("Search partner error:", err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [keyword]);

  const select = (user) => {
    setValues({
      first_name: user.first_name || user.firstName || "",
      last_name: user.last_name || user.lastName || "",
      mobile_number: (user.mobile_number || user.mobileNo || "").replace(/^91/, ""),
      email_address: user.email_address || user.emailId || "",
      address: user.address || "",
      address1: "",
      street: "",
      apartment: "",
      country_name: user.country || "India",
      state_name: user.state || "",
      city_name: user.city || "",
      zip_code: user.zipCode || "",
      area_name: user.area || "",
    });
    setResults([]);
    setKeyword("");
    showToast(`Selected ${user.first_name || "Partner"} from directory`, { type: "info" });
  };

  return (
    <FieldShell id="partner-search" label="Search existing partner (optional)" helper="Type a name, phone or email to auto-fill the form.">
      <div className="position-relative">
        <input
          id="partner-search"
          type="search"
          className="fk-input"
          placeholder="Type name, phone or email to auto-fill..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          autoComplete="off"
        />
        {searching && <Loader2 size={14} className="position-absolute" style={{ right: 12, top: 15 }} aria-label="Searching" />}
      </div>
      {results.length > 0 && (
        <ul className="list-group mt-2" role="listbox" aria-label="Matching partners">
          {results.map((user) => (
            <li key={user.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" role="option" aria-selected="false" tabIndex={0}
              onClick={() => select(user)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && select(user)}
            >
              <span>
                <strong>{user.first_name} {user.last_name}</strong>
                <span className="d-block small text-muted">
                  {user.mobile_number || user.mobileNo} • {user.email_address || user.emailId || "No email"}
                </span>
              </span>
              <span className="small text-primary fw-semibold">Select</span>
            </li>
          ))}
        </ul>
      )}
    </FieldShell>
  );
};

const FormBody = ({ kind, isEdit, saved }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      {kind === "agent" && !isEdit && (
        <Section title="Find a registered partner" hint="Optional shortcut - or fill in the details below manually.">
          <PartnerSearch />
        </Section>
      )}

      <Section title="Contact details">
        <div className="fk-grid">
          <TextField name="first_name" label="First Name" required placeholder={kind === "agent" ? "e.g. Rahul" : "e.g. Jane"} />
          <TextField name="last_name" label="Last Name" placeholder={kind === "agent" ? "e.g. Sharma" : "e.g. Smith"} />
          <TextField name="mobile_number" label="Mobile Number" required type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit number" />
          <TextField name="email_address" label="Email Address" type="email" placeholder={kind === "agent" ? "partner@example.com" : "jane@example.com"} />
        </div>
      </Section>

      <Section title={kind === "agent" ? "Location" : "Address"}>
        <div className="fk-grid">
          {kind === "agent" && (
            <>
              <TextField name="state_name" label="State" placeholder="e.g. Maharashtra" />
              <TextField name="city_name" label="City" placeholder="e.g. Mumbai" />
            </>
          )}
          <TextField name="area_name" label="Area / Locality" placeholder={kind === "agent" ? "e.g. Andheri West" : "e.g. Bandra"} />
          {!(kind === "agent" && isEdit) && (
            <TextField name="zip_code" label={kind === "agent" ? "Zip / Postal Code" : "Zip / Pin Code"} placeholder={kind === "agent" ? "e.g. 400053" : "e.g. 400050"} />
          )}
          {!(kind === "agent" && isEdit) && (
            <TextField name="address" label="Address" className="fk-col-full" placeholder={kind === "agent" ? "Building / Street address" : "Building / Flat / Street"} />
          )}
        </div>
      </Section>

      {kind === "agent" && !isEdit && (
        <div className="d-flex align-items-center gap-2 p-3 rounded-3" style={{ background: "#fffbeb", border: "1px solid #fef3c7", color: "#92400e", fontSize: "0.8125rem" }}>
          <Gift size={16} aria-hidden="true" />
          <span>Adding this partner will credit bonus reward points to your account.</span>
        </div>
      )}
    </Form>
  );
};

const NotFound = ({ listPath, label }) => (
  <div className="fk-page" style={{ maxWidth: 1200 }}>
    <div className="alert alert-warning d-flex align-items-center justify-content-between" role="alert">
      <span>This {label.toLowerCase()} could not be found. It may have been removed.</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={listPath}>
        Back to list
      </button>
    </div>
  </div>
);

const NetworkFormPage = ({ kind }) => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const config = KINDS[kind];
  const navigate = useNavigate();
  const location = useLocation();
  // Same page under both dashboards: the list to return to follows the current URL prefix
  const listPath = location.pathname.startsWith("/partner/") ? config.listPath.replace("/customer/", "/partner/") : config.listPath;
  const exit = useExitWizard(listPath);

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Edit: use the record handed over by the list, otherwise load it (direct link / refresh)
  useEffect(() => {
    if (!isEdit) return undefined;
    const fromList = location.state?.item;
    if (fromList && String(fromList.id) === String(Id)) {
      setRecord(fromRecord(kind, fromList));
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = kind === "agent" ? await getNetworkPartnerById(Id) : await getContactById(Id);
        const item = kind === "agent" ? res?.network : res?.contact;
        if (cancelled) return;
        if (res?.statusType === "SUCCESS" && item) setRecord(fromRecord(kind, item));
        else setNotFound(true);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.text || err?.message || "Unable to load this record.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [Id, isEdit, kind, location.state]);

  const initialValues = useMemo(() => record || EMPTY[kind][isEdit ? "edit" : "add"], [record, kind, isEdit]);
  const schema = useMemo(() => buildSchema(isEdit), [isEdit]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      let res;
      if (kind === "agent") res = isEdit ? await editNetworkPartner(values) : await addPartnerToNetwork(values);
      else res = isEdit ? await editContact(values) : await addContact(values);

      const ok = res?.statusType === "SUCCESS" || res?.status === "SUCCESS" || res?.network || res?.contact;
      if (!ok) {
        // Form data stays as entered
        showToast(res?.text || res?.message || `Failed to ${isEdit ? "update" : "add"} ${config.label.toLowerCase()}`, { type: "error" });
        setSubmitting(false);
        return;
      }
      if (kind === "agent" && !isEdit) {
        try {
          await triggerPartnerReward();
        } catch (rewardErr) {
          console.log("Reward hook completed:", rewardErr);
        }
      }
      showToast(
        kind === "agent"
          ? isEdit ? "Partner details updated successfully" : "Partner successfully added to your network!"
          : isEdit ? "Contact updated successfully" : "Contact added successfully!",
        { type: "success" }
      );
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(() => navigate(listPath), 0);
    } catch (err) {
      showToast(err?.response?.data?.text || `An error occurred while saving the ${config.label.toLowerCase()}`, { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound listPath={() => navigate(listPath)} label={config.label} />;
  if (loadError) {
    return (
      <div className="fk-page" style={{ maxWidth: 1200 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormPageLayout
      title={isEdit ? config.editTitle : config.newTitle}
      subtitle={isEdit ? config.editSubtitle : config.newSubtitle}
      formId={FORM_ID}
      submitLabel={kind === "agent" ? (isEdit ? "Save Changes" : "Add Partner to Network") : isEdit ? "Save Changes" : "Add Contact"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
        <FormBody kind={kind} isEdit={isEdit} saved={saved} />
      </Formik>
    </FormPageLayout>
  );
};

export default NetworkFormPage;
